import { useState, useEffect, useRef, useCallback } from 'react';
import { formatTimestamp, formatTime } from '../utils/date';
import {
  Card,
  Upload,
  Button,
  Typography,
  Space,
  Alert,
  Tabs,
  App,
  Select,
  Progress,
  Descriptions,
  Tag,
  Collapse,
  Table,
  Divider,
  Empty,
} from 'antd';
import {
  UploadOutlined,
  CloudUploadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  DownloadOutlined,
  WarningOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import {
  uploadCSV,
  getJobStatus,
  listJobs,
  runEtlUpdate,
  getEtlStatus,
  getDatabaseStats,
  type ImportJobStatus,
  type ETLStatus,
  type ImportStats,
} from '../api/import';

const { Title, Text, Paragraph } = Typography;

const FORMAT_OPTIONS = [
  { value: 'auto', label: 'Auto-detect (Recommended)' },
  { value: 'etilize_ingram', label: 'Etilize/Ingram (Juniper, etc.)' },
  { value: 'simple', label: 'Simple/Generic CSV' },
];

const POLL_INTERVAL = 1000; // 1 second

function getStatusColor(status: string) {
  switch (status) {
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    case 'processing':
      return 'blue';
    case 'pending':
    case 'validating':
      return 'orange';
    default:
      return 'default';
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircleOutlined />;
    case 'failed':
      return <CloseCircleOutlined />;
    case 'processing':
    case 'pending':
    case 'validating':
      return <LoadingOutlined spin />;
    default:
      return null;
  }
}

function isJobActive(status: string): boolean {
  return status === 'pending' || status === 'validating' || status === 'processing';
}


export function ImportPage() {
  const { message } = App.useApp();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('auto');
  const [activeJob, setActiveJob] = useState<ImportJobStatus | null>(null);
  const [jobHistory, setJobHistory] = useState<ImportJobStatus[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [etlStatus, setEtlStatus] = useState<ETLStatus | null>(null);
  const [etlPolling, setEtlPolling] = useState(false);
  const [dbStats, setDbStats] = useState<ImportStats | null>(null);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const etlPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch jobs and identify active ones
  const fetchJobs = useCallback(async () => {
    try {
      const jobs = await listJobs(50);

      // Find any active job
      const active = jobs.find((job) => isJobActive(job.status));

      if (active) {
        setActiveJob(active);
        // Start polling if not already polling
        if (!pollIntervalRef.current) {
          startPolling(active.job_id);
        }
      }

      // Store history (all jobs, including active)
      setJobHistory(jobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Fetch ETL status
  const fetchEtlStatus = useCallback(async () => {
    try {
      const status = await getEtlStatus();
      setEtlStatus(status);

      // Start polling if ETL is running
      if (status.is_running && !etlPollIntervalRef.current) {
        startEtlPolling();
      }
    } catch (error) {
      console.error('Failed to fetch ETL status:', error);
    }
  }, []);

  // Fetch current database stats
  const fetchDbStats = useCallback(async () => {
    try {
      const stats = await getDatabaseStats();
      setDbStats(stats);
    } catch (error) {
      console.error('Failed to fetch database stats:', error);
    }
  }, []);

  // ETL polling
  const startEtlPolling = () => {
    if (etlPollIntervalRef.current) {
      clearInterval(etlPollIntervalRef.current);
    }

    setEtlPolling(true);

    const poll = async () => {
      try {
        const status = await getEtlStatus();
        setEtlStatus(status);

        if (!status.is_running) {
          if (etlPollIntervalRef.current) {
            clearInterval(etlPollIntervalRef.current);
            etlPollIntervalRef.current = null;
          }
          setEtlPolling(false);

          if (status.status === 'completed') {
            message.success('ETL update completed successfully');
            // Refresh database stats after completion
            fetchDbStats();
          } else if (status.status === 'failed') {
            message.error('ETL update failed');
          }
        }
      } catch (error) {
        console.error('Failed to get ETL status:', error);
      }
    };

    etlPollIntervalRef.current = setInterval(poll, 2000);
  };

  // Load jobs and ETL status on mount
  useEffect(() => {
    fetchJobs();
    fetchEtlStatus();
    fetchDbStats();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (etlPollIntervalRef.current) {
        clearInterval(etlPollIntervalRef.current);
      }
    };
  }, [fetchJobs, fetchEtlStatus, fetchDbStats]);

  // Poll for job status
  const startPolling = (jobId: string) => {
    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const poll = async () => {
      try {
        const status = await getJobStatus(jobId);
        setActiveJob(status);

        // Update job in history
        setJobHistory((prev) =>
          prev.map((job) => (job.job_id === jobId ? status : job))
        );

        // Stop polling when job completes or fails
        if (status.status === 'completed' || status.status === 'failed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setUploading(false);

          if (status.status === 'completed') {
            message.success(
              `Import completed: ${status.products_inserted} inserted, ${status.products_updated} updated`
            );
          } else {
            message.error(`Import failed: ${status.message}`);
          }

          // Refresh history to get final state
          fetchJobs();
        }
      } catch (error) {
        console.error('Failed to get job status:', error);
      }
    };

    // Initial poll
    poll();

    // Start interval
    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select a CSV file first');
      return;
    }

    const file = fileList[0] as unknown as File;

    setUploading(true);
    setActiveJob(null);

    try {
      const response = await uploadCSV(file, selectedFormat);

      if (response.status === 'queued' && response.job_id) {
        message.info('Import job started');
        // Start polling for job status
        startPolling(response.job_id);
        setFileList([]);
      } else {
        message.error(response.message || 'Failed to queue import');
        setUploading(false);
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { error?: string; message?: string; hint?: string } };
        message?: string;
      };
      const errorMessage =
        err.response?.data?.message || err.message || 'Import failed';
      message.error(errorMessage);
      setActiveJob({
        job_id: '',
        filename: file.name,
        source_name: '',
        format_type: selectedFormat,
        status: 'failed',
        progress: 0,
        total_rows: 0,
        current_row: 0,
        products_inserted: 0,
        products_updated: 0,
        products_skipped: 0,
        errors: [errorMessage],
        error_count: 1,
        message: errorMessage,
        created_at: new Date().toISOString(),
        started_at: null,
        completed_at: null,
      });
      setUploading(false);
    }
  };

  const handleEtlUpdate = async () => {
    try {
      const response = await runEtlUpdate();

      if (response.status === 'already_running') {
        message.info('ETL process is already running');
      } else if (response.status === 'started') {
        message.success('ETL update started');
        startEtlPolling();
      } else {
        message.error(response.message || 'Failed to start ETL');
      }

      // Refresh status
      fetchEtlStatus();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      message.error(err.response?.data?.message || err.message || 'Failed to start ETL');
    }
  };

  const uploadProps: UploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
      if (!isCSV) {
        message.error('You can only upload CSV files');
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false; // Prevent auto upload
    },
    fileList,
    maxCount: 1,
    accept: '.csv',
  };

  const renderJobProgress = (job: ImportJobStatus, showTitle: boolean = true) => {
    const isRunning = isJobActive(job.status);

    return (
      <Card
        size="small"
        style={{ marginTop: showTitle ? 24 : 0 }}
        title={
          showTitle ? (
            <Space>
              {getStatusIcon(job.status)}
              <span>Import Job: {job.job_id || job.filename}</span>
              <Tag color={getStatusColor(job.status)}>
                {job.status.toUpperCase()}
              </Tag>
            </Space>
          ) : undefined
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Progress Bar */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <Text>{job.message}</Text>
              <Text type="secondary">
                {job.current_row.toLocaleString()} /{' '}
                {job.total_rows.toLocaleString()} rows
              </Text>
            </div>
            <Progress
              percent={job.progress}
              status={
                job.status === 'failed'
                  ? 'exception'
                  : job.status === 'completed'
                    ? 'success'
                    : 'active'
              }
              strokeColor={isRunning ? { from: '#108ee9', to: '#87d068' } : undefined}
            />
          </div>

          {/* Stats */}
          <Descriptions size="small" column={4} bordered>
            <Descriptions.Item label="Inserted">
              <Text type="success">{job.products_inserted}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              <Text type="warning">{job.products_updated}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Skipped">
              <Text type="secondary">{job.products_skipped}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Errors">
              <Text type={job.error_count > 0 ? 'danger' : 'secondary'}>
                {job.error_count}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          {/* Errors */}
          {job.error_count > 0 && (
            <Collapse
              size="small"
              items={[
                {
                  key: 'errors',
                  label: (
                    <Space>
                      <WarningOutlined style={{ color: '#ff4d4f' }} />
                      <Text type="danger">
                        {job.error_count} error{job.error_count !== 1 ? 's' : ''} during import
                      </Text>
                    </Space>
                  ),
                  children: (
                    <div>
                      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary">
                          {job.errors.length < job.error_count
                            ? `Showing first ${job.errors.length} of ${job.error_count} errors`
                            : `All ${job.error_count} errors`}
                        </Text>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => {
                            const content = job.errors.join('\n');
                            const blob = new Blob([content], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `import-errors-${job.job_id}.txt`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          Download Errors
                        </Button>
                      </div>
                      <Table
                        size="small"
                        pagination={{ pageSize: 10, size: 'small' }}
                        dataSource={job.errors.map((err, i) => {
                          // Parse error format "Row X: message | context"
                          const match = err.match(/^Row (\d+): (.+)$/);
                          const fullMessage = match ? match[2] : err;
                          // Split message and context by " | "
                          const [errorPart, ...contextParts] = fullMessage.split(' | ');
                          const context = contextParts.join(' | ');
                          return {
                            key: i,
                            row: match ? parseInt(match[1], 10) : i + 1,
                            error: errorPart,
                            context: context || '-',
                            raw: err,
                          };
                        })}
                        columns={[
                          {
                            title: 'Row',
                            dataIndex: 'row',
                            key: 'row',
                            width: 70,
                            sorter: (a: { row: number }, b: { row: number }) => a.row - b.row,
                          },
                          {
                            title: 'Error',
                            dataIndex: 'error',
                            key: 'error',
                            width: '30%',
                            render: (text: string) => (
                              <Text type="danger" style={{ fontSize: 12 }}>
                                {text}
                              </Text>
                            ),
                          },
                          {
                            title: 'Product Info',
                            dataIndex: 'context',
                            key: 'context',
                            ellipsis: true,
                            render: (text: string) => (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {text}
                              </Text>
                            ),
                          },
                        ]}
                      />
                    </div>
                  ),
                },
              ]}
            />
          )}

          {/* Completion message */}
          {job.status === 'completed' && (
            <Alert
              type="success"
              message="Import completed successfully"
              description={`${job.products_inserted.toLocaleString()} products inserted, ${job.products_updated.toLocaleString()} updated, ${job.products_skipped.toLocaleString()} skipped.`}
            />
          )}

          {job.status === 'failed' && job.errors.length === 0 && (
            <Alert type="error" message="Import failed" description={job.message} />
          )}
        </Space>
      </Card>
    );
  };

  const renderImportHistory = () => {
    // Filter out the active job from history display (it's shown separately)
    const completedJobs = jobHistory.filter(
      (job) => !isJobActive(job.status) && job.job_id !== activeJob?.job_id
    );

    if (loadingHistory) {
      return (
        <Card size="small">
          <Space>
            <LoadingOutlined spin />
            <Text type="secondary">Loading import history...</Text>
          </Space>
        </Card>
      );
    }

    if (completedJobs.length === 0) {
      return (
        <Card size="small">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No import history yet"
          />
        </Card>
      );
    }

    const columns = [
      {
        title: 'Job ID',
        dataIndex: 'job_id',
        key: 'job_id',
        width: 100,
        render: (id: string) => <Text code>{id}</Text>,
      },
      {
        title: 'File',
        dataIndex: 'filename',
        key: 'filename',
        ellipsis: true,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
            {status.toUpperCase()}
          </Tag>
        ),
      },
      {
        title: 'Inserted',
        dataIndex: 'products_inserted',
        key: 'products_inserted',
        width: 90,
        render: (val: number) => <Text type="success">{val.toLocaleString()}</Text>,
      },
      {
        title: 'Updated',
        dataIndex: 'products_updated',
        key: 'products_updated',
        width: 90,
        render: (val: number) => <Text type="warning">{val.toLocaleString()}</Text>,
      },
      {
        title: 'Errors',
        dataIndex: 'error_count',
        key: 'error_count',
        width: 80,
        render: (val: number) => (
          <Text type={val > 0 ? 'danger' : 'secondary'}>{val}</Text>
        ),
      },
      {
        title: 'Completed',
        dataIndex: 'completed_at',
        key: 'completed_at',
        width: 170,
        render: (val: string | null) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatTimestamp(val)}
          </Text>
        ),
      },
    ];

    return (
      <Table
        size="small"
        dataSource={completedJobs.map((job) => ({ ...job, key: job.job_id }))}
        columns={columns}
        pagination={{ pageSize: 5, size: 'small' }}
        expandable={{
          expandedRowRender: (record) => renderJobProgress(record as ImportJobStatus, false),
          rowExpandable: () => true,
        }}
      />
    );
  };

  const tabItems = [
    {
      key: 'csv',
      label: 'CSV Import',
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Paragraph>
            Upload a CSV file containing product data. The CSV should include columns for
            product information such as MPN, description, manufacturer, attributes, etc.
          </Paragraph>

          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} disabled={uploading}>
                  Select CSV File
                </Button>
              </Upload>

              <div style={{ marginTop: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  CSV Format:
                </Text>
                <Select
                  value={selectedFormat}
                  onChange={setSelectedFormat}
                  options={FORMAT_OPTIONS}
                  style={{ width: 280 }}
                  disabled={uploading}
                />
              </div>

              <Button
                type="primary"
                onClick={handleUpload}
                disabled={fileList.length === 0 || uploading}
                loading={uploading}
                icon={<CloudUploadOutlined />}
                style={{ marginTop: 16 }}
              >
                {uploading ? 'Importing...' : 'Start Import'}
              </Button>
            </Space>
          </Card>

          {/* Active Job Progress */}
          {activeJob && renderJobProgress(activeJob)}

          {/* Import History */}
          <Divider orientation="left">
            <Space>
              <HistoryOutlined />
              Import History
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={fetchJobs}
                loading={loadingHistory}
              />
            </Space>
          </Divider>
          {renderImportHistory()}

          <Alert
            type="info"
            showIcon
            message="CSV Format"
            description={
              <div>
                <Text>Expected columns (flexible mapping):</Text>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li>productid / product_id - Unique product identifier</li>
                  <li>mfgpartno / mpn - Manufacturer part number (required)</li>
                  <li>description - Product description</li>
                  <li>manufacturer - Manufacturer name</li>
                  <li>category - Product category</li>
                  <li>image_url - Primary image URL (optional)</li>
                </ul>
              </div>
            }
          />
        </Space>
      ),
    },
    {
      key: 'etl',
      label: (
        <Space>
          NIQ Brandbank
          {etlStatus?.is_running && <LoadingOutlined spin style={{ marginLeft: 4 }} />}
        </Space>
      ),
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Paragraph>
            Sync product data from NIQ Brandbank (formerly Etilize BrandBank). This downloads
            product specifications, features, images, and SKU mappings from the Brandbank FTP server.
          </Paragraph>

          <Alert
            type="info"
            showIcon
            message="About NIQ Brandbank"
            description="Brandbank is a product content provider owned by NIQ (Nielsen IQ). This sync downloads comprehensive product data including specifications, features, and distributor SKU mappings."
          />

          <Button
            type="primary"
            onClick={handleEtlUpdate}
            loading={etlPolling && !etlStatus?.is_running}
            disabled={etlStatus?.is_running}
            icon={<SyncOutlined spin={etlStatus?.is_running} />}
            size="large"
          >
            {etlStatus?.is_running ? 'Sync Running...' : 'Sync from Brandbank'}
          </Button>

          {/* Current Database Stats - show when no ETL running or when idle */}
          {dbStats && (!etlStatus || etlStatus.status === 'idle') && (
            <Card size="small" title="Current Database Content">
              <Descriptions size="small" column={3} bordered>
                <Descriptions.Item label="Products">
                  <Text strong>{dbStats.products.toLocaleString()}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Manufacturers">
                  <Text strong>{dbStats.manufacturers.toLocaleString()}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Categories">
                  <Text strong>{dbStats.categories.toLocaleString()}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Features">
                  <Text strong>{dbStats.features.toLocaleString()}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Attributes">
                  <Text strong>{dbStats.attributes.toLocaleString()}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="SKUs">
                  <Text strong>{dbStats.skus.toLocaleString()}</Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* ETL Status Card */}
          {etlStatus && etlStatus.status !== 'idle' && (
            <Card
              size="small"
              title={
                <Space>
                  {etlStatus.is_running ? <LoadingOutlined spin /> :
                   etlStatus.status === 'completed' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                   etlStatus.status === 'failed' ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> : null}
                  <span>Brandbank Sync</span>
                  <Tag color={
                    etlStatus.status === 'completed' ? 'green' :
                    etlStatus.status === 'failed' ? 'red' :
                    etlStatus.is_running ? 'blue' : 'default'
                  }>
                    {etlStatus.status.toUpperCase()}
                  </Tag>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text strong>{etlStatus.message}</Text>
                  {etlStatus.current_delta && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({etlStatus.current_delta})
                    </Text>
                  )}
                </div>

                {etlStatus.is_running && (
                  <Progress
                    percent={100}
                    status="active"
                    showInfo={false}
                    strokeColor={{ from: '#108ee9', to: '#87d068' }}
                  />
                )}

                <Descriptions size="small" column={4} bordered>
                  <Descriptions.Item label="Phase">
                    <Tag>{etlStatus.phase || 'initializing'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Downloaded">
                    <Text type="success">{etlStatus.files_downloaded}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Unzipped">
                    <Text type="warning">{etlStatus.files_unzipped}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Current File">
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {etlStatus.current_file || '-'}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>

                {etlStatus.started_at && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Started: {formatTimestamp(etlStatus.started_at)}
                    {etlStatus.last_activity && (
                      <> | Last activity: {formatTime(etlStatus.last_activity)}</>
                    )}
                  </Text>
                )}

                {etlStatus.errors.length > 0 && (
                  <Collapse
                    size="small"
                    items={[
                      {
                        key: 'errors',
                        label: (
                          <Space>
                            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                            <Text type="danger">{etlStatus.errors.length} error(s)</Text>
                          </Space>
                        ),
                        children: (
                          <div style={{ maxHeight: 200, overflow: 'auto' }}>
                            {etlStatus.errors.map((err, i) => (
                              <div key={i} style={{ fontSize: 11, marginBottom: 4 }}>
                                <Text type="danger">{err}</Text>
                              </div>
                            ))}
                          </div>
                        ),
                      },
                    ]}
                  />
                )}

                {etlStatus.warnings?.length > 0 && (
                  <Collapse
                    size="small"
                    items={[
                      {
                        key: 'warnings',
                        label: (
                          <Space>
                            <WarningOutlined style={{ color: '#faad14' }} />
                            <Text type="warning">{etlStatus.warnings.length} warning(s)</Text>
                          </Space>
                        ),
                        children: (
                          <div style={{ maxHeight: 200, overflow: 'auto' }}>
                            {etlStatus.warnings.map((warn, i) => (
                              <div key={i} style={{ fontSize: 11, marginBottom: 4 }}>
                                <Text type="warning">{warn}</Text>
                              </div>
                            ))}
                          </div>
                        ),
                      },
                    ]}
                  />
                )}

                {etlStatus.status === 'completed' && (
                  <>
                    <Alert
                      type={etlStatus.warnings?.length > 0 ? 'warning' : 'success'}
                      message={etlStatus.warnings?.length > 0 ? 'Sync completed with warnings' : 'Sync completed successfully'}
                      description={`Downloaded ${etlStatus.files_downloaded} files, processed ${etlStatus.files_unzipped} archives.${etlStatus.warnings?.length > 0 ? ' Some non-critical issues occurred (e.g., email notification failed).' : ''}`}
                    />

                    {/* Import Statistics */}
                    {(etlStatus.stats_after || etlStatus.stats_change) && (
                      <Card size="small" title="Import Statistics" style={{ marginTop: 16 }}>
                        <Descriptions size="small" column={3} bordered>
                          <Descriptions.Item label="Products">
                            <Space>
                              <Text strong>{etlStatus.stats_after?.products.toLocaleString() || 0}</Text>
                              {etlStatus.stats_change && etlStatus.stats_change.products !== 0 && (
                                <Tag color={etlStatus.stats_change.products > 0 ? 'green' : 'red'}>
                                  {etlStatus.stats_change.products > 0 ? '+' : ''}{etlStatus.stats_change.products.toLocaleString()}
                                </Tag>
                              )}
                            </Space>
                          </Descriptions.Item>
                          <Descriptions.Item label="Manufacturers">
                            <Space>
                              <Text strong>{etlStatus.stats_after?.manufacturers.toLocaleString() || 0}</Text>
                              {etlStatus.stats_change && etlStatus.stats_change.manufacturers !== 0 && (
                                <Tag color={etlStatus.stats_change.manufacturers > 0 ? 'green' : 'red'}>
                                  {etlStatus.stats_change.manufacturers > 0 ? '+' : ''}{etlStatus.stats_change.manufacturers.toLocaleString()}
                                </Tag>
                              )}
                            </Space>
                          </Descriptions.Item>
                          <Descriptions.Item label="Categories">
                            <Space>
                              <Text strong>{etlStatus.stats_after?.categories.toLocaleString() || 0}</Text>
                              {etlStatus.stats_change && etlStatus.stats_change.categories !== 0 && (
                                <Tag color={etlStatus.stats_change.categories > 0 ? 'green' : 'red'}>
                                  {etlStatus.stats_change.categories > 0 ? '+' : ''}{etlStatus.stats_change.categories.toLocaleString()}
                                </Tag>
                              )}
                            </Space>
                          </Descriptions.Item>
                          <Descriptions.Item label="Features">
                            <Space>
                              <Text strong>{etlStatus.stats_after?.features.toLocaleString() || 0}</Text>
                              {etlStatus.stats_change && etlStatus.stats_change.features !== 0 && (
                                <Tag color={etlStatus.stats_change.features > 0 ? 'green' : 'red'}>
                                  {etlStatus.stats_change.features > 0 ? '+' : ''}{etlStatus.stats_change.features.toLocaleString()}
                                </Tag>
                              )}
                            </Space>
                          </Descriptions.Item>
                          <Descriptions.Item label="Attributes">
                            <Space>
                              <Text strong>{etlStatus.stats_after?.attributes.toLocaleString() || 0}</Text>
                              {etlStatus.stats_change && etlStatus.stats_change.attributes !== 0 && (
                                <Tag color={etlStatus.stats_change.attributes > 0 ? 'green' : 'red'}>
                                  {etlStatus.stats_change.attributes > 0 ? '+' : ''}{etlStatus.stats_change.attributes.toLocaleString()}
                                </Tag>
                              )}
                            </Space>
                          </Descriptions.Item>
                          <Descriptions.Item label="SKUs">
                            <Space>
                              <Text strong>{etlStatus.stats_after?.skus.toLocaleString() || 0}</Text>
                              {etlStatus.stats_change && etlStatus.stats_change.skus !== 0 && (
                                <Tag color={etlStatus.stats_change.skus > 0 ? 'green' : 'red'}>
                                  {etlStatus.stats_change.skus > 0 ? '+' : ''}{etlStatus.stats_change.skus.toLocaleString()}
                                </Tag>
                              )}
                            </Space>
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    )}
                  </>
                )}

                {etlStatus.status === 'failed' && (
                  <Alert
                    type="error"
                    message="Sync failed"
                    description={etlStatus.message}
                  />
                )}
              </Space>
            </Card>
          )}

          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            onClick={fetchEtlStatus}
          >
            Refresh Status
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>Import Products</Title>
      <Paragraph type="secondary">
        Import product data into the system using CSV files or the Etilize ETL process.
      </Paragraph>

      <Tabs items={tabItems} />
    </div>
  );
}
